import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, CalendarDays, Truck, MapPin, PackageCheck, ArrowLeft, Phone, Clock, ClipboardCheck, FileText, Share2, Check } from 'lucide-react';
import { loadOrderById, decodeOrderToken, buildShareLink, getCurrentStepIndex, getEtaLabel, saveOrderForTracking, TrackedOrder, STEP_ORDER } from '../utils/orderTracking';
const STEPS = [{
  id: 'confirmed',
  label: 'Order Confirmed',
  icon: CheckCircle2,
  desc: 'We received your order.'
}, {
  id: 'scheduled',
  label: 'Scheduled',
  icon: CalendarDays,
  desc: 'Delivery window confirmed.'
}, {
  id: 'dispatched',
  label: 'Dispatched',
  icon: ClipboardCheck,
  desc: 'Driver assigned and loading.'
}, {
  id: 'en_route',
  label: 'En Route',
  icon: Truck,
  desc: 'Truck is on the way to your site.'
}, {
  id: 'delivered',
  label: 'Delivered',
  icon: PackageCheck,
  desc: 'Material successfully dumped.'
}] as const;
export function TrackOrder() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const location = useLocation();
  const [orderData, setOrderData] = useState<TrackedOrder | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [copied, setCopied] = useState(false);
  // Load order: localStorage first, then ?d= URL token fallback
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!orderId) return;
    // 1. localStorage
    const local = loadOrderById(orderId);
    if (local) {
      setOrderData(local);
      return;
    }
    // 2. ?d= URL token (shared tracking link)
    const search = location.search || (location.hash ? '' : '');
    let token: string | null = null;
    if (search) {
      token = new URLSearchParams(search).get('d');
    } else {
      // HashRouter quirk: search params can live inside the hash
      const hash = window.location.hash;
      const qIdx = hash.indexOf('?');
      if (qIdx >= 0) token = new URLSearchParams(hash.slice(qIdx)).get('d');
    }
    if (token) {
      const decoded = decodeOrderToken(token);
      if (decoded && decoded.orderId === orderId) {
        setOrderData(decoded);
        // Persist locally so the customer doesn't need the share link next time
        saveOrderForTracking(decoded);
      }
    }
  }, [orderId, location.search]);
  // Keep status live — recompute every 30s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const currentStepIndex = useMemo(() => orderData ? getCurrentStepIndex(orderData, now) : 0, [orderData, now]);
  const etaLabel = useMemo(() => orderData ? getEtaLabel(orderData, now) : '', [orderData, now]);
  const handleShare = async () => {
    if (!orderData) return;
    const url = buildShareLink(orderData);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `253Gravel Order ${orderData.orderId}`,
          text: 'Track my gravel delivery',
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch {
      /* user cancelled */
    }
  };
  if (!orderData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-[#fdfbf7] dark:bg-[#1b1b1b]" data-id="element-3134">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-3135">
          Order Not Found
        </h1>
        <p className="text-stone-700 dark:text-stone-300 mb-2 max-w-md" data-id="element-3136">
          We couldn't find tracking details for order {orderId} on this device.
        </p>
        <p className="text-stone-700 dark:text-stone-300 mb-6 max-w-md text-sm" data-id="element-3137">
          If you have a tracking link from your order email or text, open that
          link directly. Otherwise, call dispatch and we'll look it up.
        </p>
        <div className="flex gap-3" data-id="element-3138">
          <Link to="/track" className="px-5 py-2.5 rounded-lg border border-stone-200 dark:border-[#444] text-stone-700 dark:text-stone-300 font-medium hover:bg-stone-50 dark:hover:bg-[#222] transition-colors" data-id="element-3139">
            Try another ID
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-orange text-white font-bold hover:bg-brand-orange/90 transition-colors" data-id="element-3140">
            <Phone className="w-4 h-4" data-id="element-3141" />
            Call Dispatch
          </a>
        </div>
      </div>;
  }
  const totalSubtotal = orderData.items.reduce((sum, it) => sum + it.quantity * it.pricePerYard, 0);
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)]" data-id="element-3142">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3143">
        <Link to="/track" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors mb-8" data-id="element-3144">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3145" />
          All orders
        </Link>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4" data-id="element-3146">
          <div data-id="element-3147">
            <h1 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-3148">
              Track Order
            </h1>
            <p className="text-stone-700 dark:text-stone-300 mt-1" data-id="element-3149">
              Order #{orderData.orderId}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-3150">
            {currentStepIndex < 4 && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-brand-orange text-sm font-bold" data-id="element-3151">
                <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse mr-2" data-id="element-3152" />
                {etaLabel}
              </div>}
            <button type="button" onClick={handleShare} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 dark:bg-[#2a2a2a] border border-stone-200 dark:border-[#3a3a3a] text-stone-700 dark:text-stone-300 text-sm font-medium hover:bg-stone-200 dark:hover:bg-[#333] transition-colors" data-id="element-3153">
              {copied ? <>
                  <Check className="w-4 h-4 text-[#059F40]" data-id="element-3154" />
                  Copied
                </> : <>
                  <Share2 className="w-4 h-4" data-id="element-3155" />
                  Share
                </>}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3156">
          {/* Left Col: Map & Summary */}
          <div className="md:col-span-2 space-y-6" data-id="element-3157">
            {/* Mock Map */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] overflow-hidden shadow-sm h-64 relative" data-id="element-3158">
              <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-200 dark:from-[#2a2a2a] dark:to-[#1a1a1a]" data-id="element-3159" />
              <svg className="absolute inset-0 w-full h-full opacity-30 dark:opacity-20" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-3160">
                <path d="M10,90 Q30,50 50,50 T90,10" fill="none" stroke="#059F40" strokeWidth="2" strokeDasharray="4 4" data-id="element-3161" />
              </svg>
              <div className="absolute bottom-4 left-4 w-4 h-4 bg-brand-charcoal dark:bg-white rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-3162" />
              <div className="absolute top-4 right-4 w-4 h-4 bg-brand-orange rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-3163" />

              {currentStepIndex >= 2 && currentStepIndex < 4 && <motion.div className="absolute z-10 bg-white dark:bg-black p-2 rounded-full shadow-lg border border-stone-200 dark:border-[#444]" initial={{
              left: '10%',
              top: '80%'
            }} animate={{
              left: currentStepIndex === 3 ? '50%' : '20%',
              top: currentStepIndex === 3 ? '50%' : '70%'
            }} transition={{
              duration: 2,
              ease: 'easeInOut'
            }} data-id="element-3164">
                  <Truck className="w-5 h-5 text-[#059F40]" data-id="element-3165" />
                </motion.div>}

              <div className="absolute top-4 left-4 bg-white/90 dark:bg-black/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-stone-200 dark:border-[#444] shadow-sm" data-id="element-3166">
                <p className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-0.5" data-id="element-3167">
                  Live ETA
                </p>
                <p className="text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-3168">
                  {currentStepIndex === 4 ? 'Delivered' : etaLabel}
                </p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm" data-id="element-3169">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-4 flex items-center" data-id="element-3170">
                <FileText className="w-5 h-5 mr-2 text-brand-orange" data-id="element-3171" />
                Order Details
              </h3>

              <div className="space-y-4" data-id="element-3172">
                <div className="flex items-start gap-3" data-id="element-3173">
                  <MapPin className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-3174" />
                  <div data-id="element-3175">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-3176">
                      {orderData.address}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-3177">
                      Instructions: {orderData.instructions || 'None'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-3178">
                  <Clock className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-3179" />
                  <div data-id="element-3180">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-3181">
                      {orderData.date ? (() => {
                      // Parse YYYY-MM-DD as local time (not UTC) so the
                      // displayed weekday doesn't shift back a day in
                      // Pacific time.
                      const m = orderData.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                      const d = m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : new Date(orderData.date);
                      return d.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                      });
                    })() : 'Date TBD'}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-3182">
                      Window: {orderData.time || 'Any time'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-stone-100 dark:border-[#333] pt-4 mt-4" data-id="element-3183">
                  {orderData.items.map((item, i) => <div key={i} className="flex justify-between text-sm mb-2" data-id="element-3184">
                      <span className="text-stone-700 dark:text-stone-300" data-id="element-3185">
                        {item.quantity} yds {item.product}
                      </span>
                      <span className="font-medium text-stone-900 dark:text-white" data-id="element-3186">
                        ${(item.quantity * item.pricePerYard).toFixed(2)}
                      </span>
                    </div>)}
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-3187">
                    <span data-id="element-3188">Delivery Fee</span>
                    <span data-id="element-3189">${orderData.totalDeliveryFee.toFixed(2)}</span>
                  </div>
                  {orderData.saturdaySurcharge && orderData.saturdaySurcharge > 0 ? <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-3190">
                      <span data-id="element-3191">Saturday Delivery</span>
                      <span data-id="element-3192">${orderData.saturdaySurcharge.toFixed(2)}</span>
                    </div> : null}
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-3193">
                    <span data-id="element-3194">Tax</span>
                    <span data-id="element-3195">${orderData.estimatedTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-stone-100 dark:border-[#333] text-brand-charcoal dark:text-white" data-id="element-3196">
                    <span data-id="element-3197">Total</span>
                    <span data-id="element-3198">${orderData.finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Col: Timeline */}
          <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm h-fit" data-id="element-3199">
            <h3 className="font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-3200">
              Status
            </h3>

            <div className="relative" data-id="element-3201">
              <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-stone-100 dark:bg-[#333]" data-id="element-3202" />

              <div className="space-y-8 relative" data-id="element-3203">
                {STEPS.map((step, index) => {
                const isPast = index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const Icon = step.icon;
                return <div key={step.id} className="flex items-start gap-4" data-id="element-3204">
                      <div className="relative z-10 bg-white dark:bg-[#222] py-1" data-id="element-3205">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${isPast ? 'bg-[#059F40] border-[#059F40] text-white' : isCurrent ? 'bg-white dark:bg-[#222] border-[#059F40] text-[#059F40]' : 'bg-white dark:bg-[#222] border-stone-200 dark:border-[#444] text-stone-300 dark:text-[#555]'}`} data-id="element-3206">
                          <Icon className="w-4 h-4" data-id="element-3207" />
                        </div>
                        {isCurrent && <span className="absolute inset-0 rounded-full border-2 border-[#059F40] animate-ping opacity-20" style={{
                      top: '4px',
                      bottom: '4px'
                    }} data-id="element-3208" />}
                      </div>

                      <div className="pt-1.5" data-id="element-3209">
                        <p className={`text-sm font-bold transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-900 dark:text-white' : 'text-stone-400 dark:text-[#666]'}`} data-id="element-3210">
                          {step.label}
                        </p>
                        <p className={`text-xs mt-0.5 transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-500 dark:text-stone-400' : 'text-stone-300 dark:text-[#555]'}`} data-id="element-3211">
                          {step.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-100 dark:border-[#333]" data-id="element-3212">
              <p className="text-xs text-stone-500 mb-3" data-id="element-3213">
                Need a real-time update?
              </p>
              <a href="tel:206-619-3742" className="flex items-center justify-center w-full py-2.5 rounded-lg border border-stone-200 dark:border-[#444] text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#2a2a2a] transition-colors" data-id="element-3214">
                <Phone className="w-4 h-4 mr-2" data-id="element-3215" />
                Call Dispatch
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>;
}